import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgClass } from '@angular/common';

/**
 * TVG-135: Quick-action footer for board cards that sit behind an event.
 *
 * From Active Opportunity onwards a card represents the event, so it offers two
 * routes: "Open contact" (the parent contact) and "Open event" (the event
 * itself). `primary` decides which is emphasised — Open event becomes primary
 * once the event is at Provisional / Confirmed, since that is where the work
 * happens and going via the contact would only add clicks.
 *
 * "Open event" is hidden when there is no single event to open (e.g. a contact
 * shopping multiple venues — managed from the contact record instead).
 */
@Component({
  selector: 'app-board-card-actions',
  standalone: true,
  imports: [NgClass],
  template: `
    <div class="mt-3 flex items-center gap-2 border-t border-gray-100 pt-2 dark:border-white/10">
      @if (contactId !== null) {
        <button
          type="button"
          class="rounded-md px-2 py-1 text-xs font-medium transition-colors"
          [ngClass]="
            primary === 'contact'
              ? 'bg-brand-500 text-white hover:bg-brand-600'
              : 'text-brand-600 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/5'
          "
          (click)="$event.stopPropagation(); openContact.emit()"
          data-testid="card-action-open-contact"
        >
          Open contact
        </button>
      }
      @if (eventId !== null) {
        <button
          type="button"
          class="rounded-md px-2 py-1 text-xs font-medium transition-colors"
          [ngClass]="
            primary === 'event'
              ? 'bg-brand-500 text-white hover:bg-brand-600'
              : 'text-brand-600 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/5'
          "
          (click)="$event.stopPropagation(); openEvent.emit()"
          data-testid="card-action-open-event"
        >
          Open event
        </button>
      }
    </div>
  `,
})
export class BoardCardActionsComponent {
  @Input() contactId: number | null = null;
  @Input() eventId: number | null = null;
  @Input() primary: 'contact' | 'event' = 'contact';
  @Output() openContact = new EventEmitter<void>();
  @Output() openEvent = new EventEmitter<void>();
}
